import { useMutation, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'

import { patientKeys } from './query-key-factory'

import { MutationConfig } from '@/lib/react-query'
import { supabase } from '@/lib/supabase-client'
import { logger } from '@/logger'
import { useAuthenticatedUser } from '@/state/session'
import { Gender } from '@/types/user'

export const patientInputSchema = z.object({
  name: z.string().min(1, 'Le titre est requis').max(70, 'Le titre doit contenir au plus 70 caractères'),
  age: z.coerce.number().min(1, 'Requis'),
  gender: z.custom<Gender>(),
})

export const createPatientInputSchema = z.object({
  pid: z.string().min(1, 'Required'),
  ...patientInputSchema.shape,
})

export type CreatePatientInput = z.infer<typeof createPatientInputSchema>

export const createPatient = async ({ data }: { data: CreatePatientInput }) => {
  try {
    const { error, data: patient } = await supabase.from('patients').insert({
      practician_id: data.pid,
      name: data.name,
      age: data.age,
      gender: data.gender,
    })

    if (error) {
      logger.error('Error creating patient', {
        errorMessage: error,
        data,
      })
      throw error
    }

    logger.info('Patient created successfully', { patient })
    return patient
  } catch (error) {
    logger.error('Unexpected error creating patient', { error, data })
    throw error
  }
}

type UseCreatePatientOptions = {
  mutationConfig?: MutationConfig<typeof createPatient>
}

export const useCreatePatient = ({ mutationConfig }: UseCreatePatientOptions) => {
  const queryClient = useQueryClient()
  const { supaUser } = useAuthenticatedUser()

  const { onSuccess, ...restConfig } = mutationConfig || {}

  return useMutation({
    onSuccess: (...args) => {
      queryClient.refetchQueries({
        queryKey: patientKeys.all,
      })
      onSuccess?.(...args)
    },
    ...restConfig,
    mutationFn: createPatient,
    meta: { affectsSubscription: true, userId: supaUser.id },
  })
}
